'use client';

import { useTranslations } from 'i11n';
import { useCallback } from 'react';
import { Button, Checkbox, FormLabel, ScrollArea } from 'ui';

/**
 * Grilla de casillas para elegir las columnas de un reporte CSV. El catálogo lo
 * define el backend (`piramid-constants`); lo seleccionado se devuelve siempre
 * en el orden canónico del catálogo, no en el orden en que se tildó.
 */
export const ReportColumnsPicker = <Column extends string>({
  columns,
  value,
  onChange,
  getLabel,
}: {
  columns: readonly Column[];
  value: Column[];
  onChange: (columns: Column[]) => void;
  getLabel: (column: Column) => string;
}) => {
  const t = useTranslations();

  const areAllColumnsSelected = value.length === columns.length;

  const toggleAllColumns = useCallback(
    () => onChange(areAllColumnsSelected ? [] : Array.from(columns)),
    [areAllColumnsSelected, columns, onChange],
  );

  const toggleColumn = useCallback(
    (column: Column, checked: boolean) =>
      onChange(
        columns.filter((candidate) =>
          candidate === column ? checked : value.includes(candidate),
        ),
      ),
    [columns, onChange, value],
  );

  return (
    <>
      <div className='mb-4 flex flex-row items-center justify-between gap-2'>
        <FormLabel className='text-base'>
          {t('exported-report.columns_count', { count: value.length })}
        </FormLabel>
        <Button
          type='button'
          variant='link'
          size='sm'
          onClick={toggleAllColumns}
        >
          {areAllColumnsSelected
            ? t('claims.export.deselect_all')
            : t('claims.export.select_all')}
        </Button>
      </div>
      <ScrollArea type='always' className='h-96' orientation='vertical'>
        <div className='grid grid-cols-3 gap-4'>
          {columns.map((column) => (
            <label
              key={column}
              className='flex flex-row items-start space-x-3 text-sm font-normal'
            >
              <Checkbox
                checked={value.includes(column)}
                onCheckedChange={(checked) =>
                  toggleColumn(column, checked === true)
                }
              />
              <span>{getLabel(column)}</span>
            </label>
          ))}
        </div>
      </ScrollArea>
    </>
  );
};
